import { isPointInPolygon } from "geolib";
import store from "../store";
import { format } from "date-fns";

/**
 * @typedef {object} Point
 * @prop {number} lat - Latitud
 * @prop {number} lng - Longitud
 */

/**
 * @typedef {object} District
 * @prop {string} name - Nombre
 * @prop {Point[]} polygon - Perímetro expresado en puntos
 */

/**
 * @typedef {"programmed" | "sameday" | "express" | "mercadoflex"} ServiceId
 */

/**
 * Obtener distrito por punto
 * @param {Point} point
 * @returns {District}
 */
export const getDistrictByPoint = point => {
  return store.getters["business/districts"].find(district => {
    return isPointInPolygon(point, district.polygon);
  });
};

/**
 * Obtener un servicio por su ID
 * @param {ServiceId} id - ID de servicio
 */
export const getServiceById = id => {
  const services = store.state.business.services;
  if (!services) return;
  return services.find(s => s.id === id);
};

/**
 * Obtener zona de servicio por nombre de distrito
 * @param {ServiceId} serviceId - Nombre del Servicio
 * @param {string} districtName - Nombre del Distrito
 */
export const getServiceZoneByDistrictName = (serviceId, districtName) => {
  const service = getServiceById(serviceId);
  return service.zones.find(z => z.districts.includes(districtName));
};

/**
 * Obtener zona de servicio por coordenada de punto
 * @param {ServiceId} serviceId - Nombre del Servicio
 * @param {Point} point - Punto
 */
export const getServiceZoneByPoint = (serviceId, point) => {
  const zone = getDistrictByPoint(point);
  if (!zone) return;
  return getServiceZoneByDistrictName(serviceId, zone.name);
};

/**
 * Obtener toda la data relacionada con un servicio y un punto
 * @param {ServiceId} serviceId - ID de Servicio
 * @param {Point} point - Punto
 */
export const getServiceDataByPoint = (serviceId, point) => {
  const service = getServiceById(serviceId);
  const district = getDistrictByPoint(point);
  const zone = service.zones.find(z => z.districts.includes(district.name));
  return { service, zone, district };
};

/**
 * Obtener el precio de partida de un servicio
 * @param {ServiceId} serviceId - ID de Servicio
 */
export const getServiceStartingPrice = (serviceId) => {
  const service = getServiceById(serviceId)
    if (!service) return
    const orderPricing = service.pricing["moto"].order;
    switch (orderPricing.type) {
      case "evaluatex-formula":
        return orderPricing.customer.constants.basePrice
      case "zone-based":
        return Math.min(...orderPricing.customer)
      case "fixed-amount":
        return orderPricing.customer
    }
};

/**
 * Revisar si un flujo (pedido, recojo, envío) se encuentra disponible
 * @param {ServiceId} serviceId - ID de Servicio
 */
export const isServiceAvailable = (serviceId) => {
  const service = getServiceById(serviceId);
  if (!service) return false;
  const limaNow = new Date();
  const workingDay = service.schedule.weekdays
      .find(wd => wd.day === limaNow.getDay())
  if (!workingDay) return false
  const time = format(new Date(), "HH:mm");
  return time >= workingDay.open && time <= workingDay.close;
};
